import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import {
  DatesSetArg,
  EventClickArg,
  EventContentArg,
  DayCellContentArg,
  EventHoveringArg,
} from '@fullcalendar/core';

import Box from '@mui/material/Box';
import { alpha, useTheme } from '@mui/material/styles';
import { Typography, LinearProgress } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { fDate, fAPIDate } from 'src/utils/format-time';
import { fCurrencyRupees } from 'src/utils/format-number';

import { IIssuedChequeQuery, fetchIssuedChequesForCalendar } from 'src/redux/slices/issued-cheque';

import { IIssuedChequeListItem, getIssuedChequeStatusColor } from 'src/types/issued-cheque';

export type CalendarView = 'dayGridMonth' | 'dayGridWeek';

export type CalendarRange = {
  view: CalendarView;
  // First day of the visible month / week.
  date: string;
  start: string;
  end: string;
};

type Props = {
  filters: IIssuedChequeQuery;
  initialView: CalendarView;
  initialDate?: string;
  onRangeChange: (range: CalendarRange) => void;
};

// Banks are closed on Saturdays and Sundays, so a cheque dated then is banked
// on the Monday. (Public holidays aren't taken into account.)
export const getBankingDate = (chequeDate: string | Date) => {
  const date = new Date(chequeDate);
  const day = date.getDay();

  if (day === 6) date.setDate(date.getDate() + 2);
  if (day === 0) date.setDate(date.getDate() + 1);

  return date;
};

// A short bank name keeps event chips on one line.
const shortBank = (bank: string) => (bank.startsWith('Bank') ? 'BOC' : 'PB');

export default function IssuedChequeCalendar({
  filters,
  initialView,
  initialDate,
  onRangeChange,
}: Props) {
  const theme = useTheme();
  const router = useRouter();

  // Hovering a cheque hides the others and greens its banking day. This is
  // done on the DOM directly: re-rendering the calendar on every hover would
  // rebuild the event elements and drop the classes.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);

  const [range, setRange] = useState<{ start: string; end: string } | null>(null);
  const [cheques, setCheques] = useState<IIssuedChequeListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // The filter object is rebuilt every render; its parts are the real inputs.
  const { generalSearch, status, type, bank } = filters;

  useEffect(() => {
    if (!range) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(false);

    fetchIssuedChequesForCalendar({
      generalSearch,
      status,
      type,
      bank,
      startDate: range.start,
      endDate: range.end,
    })
      .then((rows) => {
        if (!cancelled) setCheques(rows);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [range, generalSearch, status, type, bank]);

  // Per-day totals shown beside the day number.
  const dayTotals = useMemo(
    () =>
      cheques.reduce((totals, cheque) => {
        const key = fAPIDate(cheque.chequeDate);
        totals.set(key, (totals.get(key) ?? 0) + cheque.amountInCents);
        return totals;
      }, new Map<string, number>()),
    [cheques]
  );

  const handleDatesSet = useCallback(
    (arg: DatesSetArg) => {
      // `end` is exclusive.
      const end = new Date(arg.end);
      end.setDate(end.getDate() - 1);

      const next = { start: fAPIDate(arg.start), end: fAPIDate(end) };

      setRange((prev) => (prev?.start === next.start && prev?.end === next.end ? prev : next));

      onRangeChange({
        view: arg.view.type as CalendarView,
        date: fAPIDate(arg.view.currentStart),
        ...next,
      });
    },
    [onRangeChange]
  );

  const events = useMemo(
    () =>
      cheques.map((cheque) => ({
        id: cheque._id,
        title: cheque.payee,
        start: cheque.chequeDate,
        allDay: true,
        backgroundColor: theme.palette[getIssuedChequeStatusColor(cheque.status)].main,
        borderColor: theme.palette[getIssuedChequeStatusColor(cheque.status)].main,
        extendedProps: { cheque },
      })),
    [cheques, theme]
  );

  const clearHover = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    wrapper.classList.remove('ic-hovering');
    wrapper.querySelectorAll('.ic-hovered').forEach((el) => el.classList.remove('ic-hovered'));
    wrapper
      .querySelectorAll('.ic-banking-day')
      .forEach((el) => el.classList.remove('ic-banking-day'));

    if (hintRef.current) hintRef.current.textContent = '';
  }, []);

  const handleEventEnter = useCallback((arg: EventHoveringArg) => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const cheque = arg.event.extendedProps.cheque as IIssuedChequeListItem;
    const bankingDate = getBankingDate(cheque.chequeDate);

    wrapper.classList.add('ic-hovering');
    arg.el.classList.add('ic-hovered');
    wrapper
      .querySelectorAll(`[data-date="${fAPIDate(bankingDate)}"]`)
      .forEach((el) => el.classList.add('ic-banking-day'));

    if (hintRef.current) {
      const movedToMonday = fAPIDate(bankingDate) !== fAPIDate(cheque.chequeDate);

      hintRef.current.textContent = movedToMonday
        ? `Cheque ${cheque.chequeNumber} is dated ${fDate(
            cheque.chequeDate,
            'EEE, dd MMM'
          )} and will be banked on ${fDate(bankingDate, 'EEE, dd MMM')}`
        : `Cheque ${cheque.chequeNumber} will be banked on its cheque date, ${fDate(
            bankingDate,
            'EEE, dd MMM'
          )}`;
    }
  }, []);

  const renderEvent = (arg: EventContentArg) => {
    const cheque = arg.event.extendedProps.cheque as IIssuedChequeListItem;

    return (
      <Box sx={{ px: 0.5, py: 0.25, overflow: 'hidden', color: 'common.white' }}>
        <Typography variant="caption" component="div" noWrap fontWeight={700}>
          {fCurrencyRupees(cheque.amountInCents)}
        </Typography>

        <Typography variant="caption" component="div" noWrap>
          {cheque.payee}
        </Typography>

        <Typography variant="caption" component="div" noWrap sx={{ opacity: 0.8 }}>
          {shortBank(cheque.bank)} {cheque.chequeNumber} · {cheque.status}
        </Typography>
      </Box>
    );
  };

  const renderDayCell = (arg: DayCellContentArg) => {
    const total = dayTotals.get(fAPIDate(arg.date));

    return (
      <Box sx={{ textAlign: 'right' }}>
        <Typography variant="caption" component="span" fontWeight={600}>
          {arg.dayNumberText}
        </Typography>

        {total ? (
          <Typography variant="caption" component="div" color="text.secondary">
            {fCurrencyRupees(total)}
          </Typography>
        ) : null}
      </Box>
    );
  };

  return (
    <Box
      ref={wrapperRef}
      sx={{
        p: 2.5,
        '& .fc': {
          '--fc-border-color': theme.palette.divider,
          '--fc-page-bg-color': 'transparent',
          '--fc-neutral-bg-color': alpha(theme.palette.grey[500], 0.08),
          '--fc-today-bg-color': alpha(theme.palette.primary.main, 0.08),
          '--fc-button-bg-color': theme.palette.primary.main,
          '--fc-button-border-color': theme.palette.primary.main,
          '--fc-button-hover-bg-color': theme.palette.primary.dark,
          '--fc-button-hover-border-color': theme.palette.primary.dark,
          '--fc-button-active-bg-color': theme.palette.primary.darker,
          '--fc-button-active-border-color': theme.palette.primary.darker,
          fontFamily: 'inherit',
        },
        '& .fc .fc-toolbar-title': { fontSize: theme.typography.h5.fontSize },
        '& .fc .fc-button': { textTransform: 'none' },
        '& .fc .fc-event': { cursor: 'pointer', marginBottom: '2px' },
        '& .fc .fc-daygrid-day-top': { flexDirection: 'row-reverse' },
        '& .fc .fc-col-header-cell-cushion, & .fc .fc-more-link': { color: 'inherit' },
        '& .fc-dayGridWeek-view .fc-daygrid-day-frame': { minHeight: 320 },
        // While one cheque is hovered, hide the rest and green its banking day.
        '&.ic-hovering .fc-event:not(.ic-hovered)': { visibility: 'hidden' },
        '& .fc .fc-daygrid-day.ic-banking-day': {
          backgroundColor: alpha(theme.palette.success.main, 0.28),
          boxShadow: `inset 0 0 0 2px ${theme.palette.success.main}`,
        },
      }}
    >
      <Box sx={{ height: 4, mb: 1 }}>{loading && <LinearProgress />}</Box>

      <Typography
        ref={hintRef}
        variant="body2"
        sx={{ minHeight: 24, mb: 1, color: 'success.dark', fontWeight: 600 }}
      />

      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          Could not load the cheques for this period.
        </Typography>
      )}

      <FullCalendar
        plugins={[dayGridPlugin]}
        initialView={initialView}
        initialDate={initialDate}
        firstDay={1}
        height="auto"
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,dayGridWeek',
        }}
        buttonText={{ today: 'Today', month: 'Month', week: 'Week' }}
        views={{
          dayGridMonth: { dayMaxEvents: 3 },
          dayGridWeek: { dayMaxEvents: false },
        }}
        events={events}
        eventContent={renderEvent}
        dayCellContent={renderDayCell}
        datesSet={handleDatesSet}
        eventMouseEnter={handleEventEnter}
        eventMouseLeave={clearHover}
        eventClick={(arg: EventClickArg) =>
          router.push(paths.dashboard.issuedCheque.view(arg.event.id))
        }
      />
    </Box>
  );
}
